import { useEffect, useLayoutEffect, useState } from "react";
import { Annotation } from "../../types/annotation";
import { useAnnotationEdit } from "../../components/thermal/useAnnotationEdit";
import { useAnnotationTime } from "../../components/thermal/useAnnotationTime";
import { useVideoPlayback } from "../../components/thermal/useVideoPlayback";
import { AnnotationList } from "../../components/thermal/AnnotationList";
import { AnnotationTimeline } from "../../components/thermal/timeline/AnnotationTimeline";
import { NewAnnotation } from "../../components/thermal/NewAnnotation";
import { VideoPlayer } from "../../components/thermal/VideoPlayer";
import { PlaylistService } from "../../apicalls/thermalService";
import { ThermalBreadcrumb } from "../../components/thermal/ThermalBreadCrumb";
import { VideoTabs } from "../../components/thermal/VideoTabs";
import { useAppContext } from "../../Context";
import AddAnnotationSideNav from "../../components/thermal/AnnotationSideNavs/AddAnnotationSideNav";
import { SubmitNewAnnotation } from "../../components/panels/PanelCTAs";
import moment from "moment";
import { useParams } from "react-router-dom";
import { Participant } from "./Participants";

interface AnnotatorProps {
    url: string;
    initialAnnotations?: Annotation[];
  }

function GenerateAnnotations(startTime: Date, endTime: Date) {

    const faceAnnotations: Annotation[] = [
        // REM Sleep Observations
        {
            id: 'f1',
            startTime: new Date(startTime.getTime() + 60 * 1000),
            endTime: new Date(startTime.getTime() + 90 * 1000),
            text: "Rapid eye movement phase",
            comments: "Clear REM sleep pattern observed with characteristic eye movements",
            color: "#FF5733",
            createdAt: new Date(startTime.getTime() + 60 * 1000),
            createdBy: "Sarah Johnson",
            lastEditedBy: "Sarah Johnson",
            userId: "caregiver1",
        },
        // Facial Expressions
        {
            id: 'f2',
            startTime: new Date(startTime.getTime() + 180 * 1000),
            endTime: new Date(startTime.getTime() + 195 * 1000),
            text: "Facial grimacing",
            comments: "Brief facial expression indicating possible discomfort",
            color: "#33FF57",
            createdAt: new Date(startTime.getTime() + 180 * 1000),
            createdBy: "Michael Chen",
            lastEditedBy: "Michael Chen",
            userId: "caregiver2",
        },
        // Sleep State Changes
        {
            id: 'f3',
            startTime: new Date(startTime.getTime() + 300 * 1000),
            endTime: new Date(startTime.getTime() + 360 * 1000),
            text: "Extended yawning episode",
            comments: "Multiple yawns observed in succession, indicating possible sleep state transition",
            color: "#3357FF",
            createdAt: new Date(startTime.getTime() + 300 * 1000),
            createdBy: "Emily Rodriguez",
            lastEditedBy: "Emily Rodriguez",
            userId: "caregiver3",
        },
        // Additional Face Annotations
        {
            id: 'f4',
            startTime: new Date(startTime.getTime() + 420 * 1000),
            endTime: new Date(startTime.getTime() + 450 * 1000),
            text: "Eye fluttering sequence",
            comments: "Rapid eye fluttering observed during light sleep phase",
            color: "#9B59B6",
            createdAt: new Date(startTime.getTime() + 420 * 1000),
            createdBy: "Sarah Johnson",
            lastEditedBy: "Sarah Johnson",
            userId: "caregiver1",
        },
        {
            id: 'f5',
            startTime: new Date(startTime.getTime() + 480 * 1000),
            endTime: new Date(startTime.getTime() + 510 * 1000),
            text: "Mouth breathing episode",
            comments: "Extended period of mouth breathing noted, possible airway obstruction",
            color: "#E74C3C",
            createdAt: new Date(startTime.getTime() + 480 * 1000),
            createdBy: "Michael Chen",
            lastEditedBy: "Michael Chen",
            userId: "caregiver2",
        }
      ];
    return faceAnnotations;
}

const VideoAnnotationPage = () => {
    const navContext = useAppContext();

    useLayoutEffect(() => {
        navContext.setFullScreen(false);
    }, []);

    const { participantID, annotationDate } = useParams();

    const participants: Participant[] = [
        {
          id: 'p1',
          title: 'Matthew S.',
          duration: '3 days',
          lastUpdated: '5d ago',
          progress: 22,
          status: 'active',
          annotations: [],
        },
        // Insert new participant at index 1
        {
          id: 'p_new1',
          title: 'Jamie P.',
          duration: '4 days',
          lastUpdated: '4d ago',
          progress: 30,
          status: 'active',
          annotations: [],
        },
        // Insert new participant at index 2
        {
          id: 'p_new2',
          title: 'Jordan W.',
          duration: '2 days',
          lastUpdated: '7d ago',
          progress: 40,
          status: 'active',
          annotations: [],
        },
        {
          id: 'p2',
          title: 'Daylan G.',
          duration: '3 days',
          lastUpdated: '5d ago',
          progress: 47,
          status: 'active',
          annotations: [],
        },
        {
          id: 'p3',
          title: 'John R.',
          duration: '2 days',
          lastUpdated: '6d ago',
          progress: 35,
          status: 'active',
          annotations: [],
        },
        {
          id: 'p4',
          title: 'Yolanda J.',
          duration: '3 days',
          lastUpdated: '5d ago',
          progress: 18,
          status: 'active',
          annotations: [],
        },
        {
          id: 'p5',
          title: 'Chelsea J.',
          duration: '3 days',
          lastUpdated: '5d ago',
          progress: 29,
          status: 'active',
          annotations: [],
        },
        {
          id: 'p6',
          title: 'Robert K.',
          duration: '2 days',
          lastUpdated: '6d ago',
          progress: 50,
          status: 'active',
          annotations: [],
        },
        {
          id: 'p7',
          title: 'Kaelah B.',
          duration: '2 days',
          lastUpdated: '6d ago',
          progress: 14,
          status: 'active',
          annotations: [],
        },
        {
          id: 'p8',
          title: 'Alexander B.',
          duration: '2 days',
          lastUpdated: '6d ago',
          progress: 40,
          status: 'active',
          annotations: [],
        },
        {
          id: 'p9',
          title: 'Brent S.',
          duration: '2 days',
          lastUpdated: '6d ago',
          progress: 33,
          status: 'active',
          annotations: [],
        },
        {
          id: 'p10',
          title: 'John P. K.',
          duration: '2 days',
          lastUpdated: '6d ago',
          progress: 10,
          status: 'active',
          annotations: [],
        },
        {
          id: 'p11',
          title: 'Brad M.',
          duration: '2 days',
          lastUpdated: '6d ago',
          progress: 55,
          status: 'active',
          annotations: [],
        },
        {
          id: 'p12',
          title: 'Jopseph D.',
          duration: '2 days',
          lastUpdated: '6d ago',
          progress: 27,
          status: 'active',
          annotations: [],
        },
        {
          id: 'p13',
          title: 'Emma A.',
          duration: '3 days',
          lastUpdated: '5d ago',
          progress: 38,
          status: 'active',
          annotations: [],
        },
        {
          id: 'p14',
          title: 'Michael F.',
          duration: '3 days',
          lastUpdated: '5d ago',
          progress: 44,
          status: 'active',
          annotations: [],
        },
        {
          id: 'p15',
          title: 'Mickael R.',
          duration: '2 days',
          lastUpdated: '6d ago',
          progress: 31,
          status: 'active',
          annotations: [],
        },
      ];

    const [participant, setParticipant] = useState<Participant | null>(null);

    const [serviceDate, setServiceDate] = useState<string | null>(null);


    useEffect(() => {
        const participant = participants.find(p => p.id === participantID);
        setParticipant(participant);
    }, [participantID]);

    useEffect(() => {
        setServiceDate(moment(annotationDate).format('MM-DD-YYYY'));
    }, [annotationDate]);

    const [showAnnotation, setShowAnnotation] = useState(false);
    const [videoRef, setVideoRef] = useState<HTMLVideoElement | null>(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(3600);
    const [annotations, setAnnotations] = useState<Annotation[]>([]);
    const [activeAnnotationId, setActiveAnnotationId] = useState<string | null>(null);

    const [url, setUrl] = useState('https://test-streams.mux.dev/x36xhzz/x36xhzz.m3u8');

    const [playlist, setPlaylist] = useState<string | null>(null);
    const [keyPairId, setKeyPairId] = useState<string | null>(null);
    const [policyId, setPolicyId] = useState<string | null>(null);
    const [signature, setSignature] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const [streamStart, setStreamStart] = useState<Date | null>(null);
    const [streamEnd, setStreamEnd] = useState<Date | null>(null);

    const breadcrumbItems = [
        { name: "Participants", href: "/thermal/participants" },
        { name: participant?.title, href: `/thermal/participants/${participantID}` },
        { name: serviceDate, href: `/thermal/participants/${participantID}/${serviceDate}/annotate` },
      ];

    const [activeTab, setActiveTab] = useState<"face" | "body">("face");
  
    useEffect(() => {
        const fetchPlaylist = async () => {
          try {
            setIsLoading(true);
            setError(null);

            const currentTime = Date.now();
            setStreamStart(new Date(currentTime - 3600000));
            setStreamEnd(new Date(currentTime));

            // const faceAnnotations = GenerateAnnotations(new Date(currentTime - 3600000), new Date(currentTime));
            // setAnnotations(faceAnnotations);
            
            const now = Math.floor(Date.now() / 1000);
            const oneHourAgo = now - 3600;

            // change from and two to be from midnight to 4 am
            const from = new Date(new Date().setHours(0, 0, 0, 0));
            const to = new Date(new Date().setHours(4, 0, 0, 0));

            setStreamStart(from);
            setStreamEnd(to);

            const faceAnnotations = GenerateAnnotations(from, to);
            setAnnotations(faceAnnotations);
    
            const playlistData = await PlaylistService.getPlaylist({
              hubId: 'mfa-hub-9UTmHv',
              from: from.getTime(),
              to: to.getTime(),
              cameraId: activeTab === "face" ? "face" : "body"
            });
    
            console.log(playlistData);
            console.log(playlistData.headers);
            console.log(playlistData.headers.keyPairId);
            console.log(playlistData.headers.policy);
            console.log(playlistData.headers.signature);
    
            setPlaylist(playlistData.data);
            setKeyPairId(playlistData.headers.keyPairId);
            setPolicyId(playlistData.headers.policy);
            setSignature(playlistData.headers.signature);

            // Create a Blob URL from the playlist content
            const blob = new Blob([playlistData.data], { type: 'application/vnd.apple.mpegurl' });
            const blobUrl = URL.createObjectURL(blob);
            setUrl(blobUrl);
            
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load playlist');
          } finally {
            setIsLoading(false);
          }
        };
    
        fetchPlaylist();
      }, [activeTab]);
    

    const {
        editingAnnotation,
        handleAnnotationClick,
        handleEditClose,
        handleEditSave
      } = useAnnotationEdit({
        annotations,
        onUpdateAnnotation: (updated) => {
          setAnnotations(prev => prev.map(a => 
            a.id === updated.id ? updated : a
          ));
        }
    });
    
    const isAnnotating = showAnnotation || !!editingAnnotation;

    const { play, pause } = useVideoPlayback({
        videoRef: { current: videoRef },
        isAnnotating
      });
    
    const {
        startTime: previewStartTime,
        endTime: previewEndTime,
        updateStartTime,
        updateEndTime
      } = useAnnotationTime({
        currentTime,
        duration,
        onSeek: (time) => {
          if (videoRef) {
            videoRef.currentTime = time;
          }
        },
        enabled: showAnnotation
      });

      const handleAnnotationOpen = () => {
        navContext.setPanelSize('tw-max-w-400px');
        navContext.setSideNavTheme(3);
        navContext.setSideNavTitle("Add Annotation");
        navContext.setSideNav( <AddAnnotationSideNav date={moment().format('M-D-YYYY')} currentTime={new Date(streamStart.getTime() + currentTime * 1000)} /> );
        navContext.setSideNavActions(null);
        navContext.setSideNavBottom(<SubmitNewAnnotation onSubmit={handleAnnotationSave}/>);
        navContext.openSideNav();
        pause();
        //setShowAnnotation(true);
      };
    
      const handleAnnotationClose = () => {
        setShowAnnotation(false);
      };

      const handleSeek = (time: number) => {
        if (videoRef) {
          videoRef.currentTime = time;
        }
      };
    
      const handleAnnotationSelect = (id: string) => {
        const annotation = annotations.find(a => a.id === id);
        if (annotation) {
          handleSeek((annotation.startTime.getTime() - streamStart.getTime()) / 1000);
          setActiveAnnotationId(id);
        }
        handleAnnotationClick(id);
      };

      const handleAnnotationSave = (annotation: Omit<Annotation, 'id' | 'color' | 'createdAt' | 'createdBy' | 'lastEditedBy' | 'userId'>) => {
        const newAnnotation: Annotation = {
          ...annotation,
          id: crypto.randomUUID(),
          color: `#${Math.floor(Math.random()*16777215).toString(16)}`,
          createdAt: new Date(),
          createdBy: 'current-user',
          lastEditedBy: 'current-user',
          userId: 'current-user'
        };
        
        setAnnotations(prev => [...prev, newAnnotation]);
        setShowAnnotation(false);
      };
    


    return (
    <div className="tw-min-h-screen tw-bg-gray-100 tw-py-8">
      <div className="tw-max-w-4xl tw-p-4">
        <ThermalBreadcrumb items={breadcrumbItems} />
        {/* <h1 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-8">
          Video Annotation
        </h1> */}
      </div>

      <VideoTabs
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab)}
      />

      <div className="tw-flex tw-gap-6 tw-w-full tw-p-4">
        <div className="tw-flex tw-flex-col tw-w-full tw-gap-6">

        <div className="tw-space-y-6">
            <div className="tw-bg-card tw-rounded-lg tw-shadow-sm tw-overflow-hidden">
                <VideoPlayer 
                    url={url}
                    keyPairId={keyPairId}
                    policy={policyId}
                    signature={signature}
                    onAnnotationClick={showAnnotation ? handleAnnotationClose : handleAnnotationOpen}
                    onVideoRef={setVideoRef}
                    onTimeUpdate={setCurrentTime}
                    onDurationChange={setDuration}
                    isAnnotationOpen={isAnnotating}
                />
            </div>
        </div>

        <div className="tw-bg-card tw-rounded-lg tw-p-4 tw-shadow-sm">
          <AnnotationTimeline
            duration={duration}
            currentTime={currentTime}
            annotations={annotations}
            streamStart={streamStart}
            streamEnd={streamEnd}
            previewStartTime={showAnnotation ? previewStartTime : undefined}
            previewEndTime={showAnnotation ? previewEndTime : undefined}
            onSeek={handleSeek}
            onAnnotationClick={handleAnnotationSelect}
            //activeAnnotationId={activeAnnotationId}
          />
        </div>

        {/* {showAnnotation && (
          <NewAnnotation
            currentTime={currentTime}
            duration={duration}
            //onSave={handleAnnotationSave}
            onCancel={handleAnnotationClose}
            onSeek={handleSeek}
            onStartTimeChange={updateStartTime}
            onEndTimeChange={updateEndTime}
          />
        )} */}


        <AnnotationList 
            annotations={annotations}
            onAnnotationClick={handleAnnotationSelect}
            activeAnnotationId={activeAnnotationId}
            />

        </div>
{/* 
        <div className="tw-w-2/6 tw-shrink-0 tw-px-4">
            
        </div> */}
      </div>

      


    </div>
  );
};

export default VideoAnnotationPage;
